// ABOUTME: Warehouse-side service desk history. Left: relations with aggregated stats (sortable, searchable).
// ABOUTME: Right: ShDataGrid2 of CLOSED tickets for the selected relation. Click a row to open in MTicketDetailBottom.

import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DropdownBaseV2 } from "../../../../../components/theme/dropdowns/Dropdown";
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../components/theme/TabView3/IconColorGenerator";
import { tokens } from "../../../../../theme";
import { localeSettings } from "../../../../../utils/locales";

import {
    useGetWarehouseRelationsWithTicketStatsQuery,
    useGetWarehouseRelationTicketHistoryQuery,
} from "../../../../../newapi/warehouse/supportTicketApiSlice";

import { RelationHistoryCard } from "../components/RelationHistoryCard";
import MTicketDetailBottom from "../components/modals/MTicketDetailBottom";
import PriorityBadge from "../components/PriorityBadge";

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const SORT_OPTIONS = [
    {
        id: "totalTickets:DESC",
        sortBy: "totalTickets",
        sortOrder: "DESC",
        labelKey: "sort_most_tickets_total",
    },
    {
        id: "openTickets:DESC",
        sortBy: "openTickets",
        sortOrder: "DESC",
        labelKey: "sort_most_open_tickets",
    },
    {
        id: "lastResolvedAt:DESC",
        sortBy: "lastResolvedAt",
        sortOrder: "DESC",
        labelKey: "sort_latest_resolved",
    },
    {
        id: "lastTicketActivityAt:DESC",
        sortBy: "lastTicketActivityAt",
        sortOrder: "DESC",
        labelKey: "sort_latest_activity",
    },
    {
        id: "name:ASC",
        sortBy: "name",
        sortOrder: "ASC",
        labelKey: "sort_name_az",
    },
];

const TypeBadge = ({ type }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    if (!type) return null;
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                background: generateColorFromTitle(
                    type,
                    theme.palette.mode,
                    90
                ),
                borderRadius: 5,
                px: 1.5,
                py: 0.5,
                height: 24,
                lineHeight: "24px",
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: generateIconColorFromTitle(
                        type,
                        theme.palette.mode
                    ),
                    flexShrink: 0,
                }}
            />
            <Text bold sx={{ fontSize: 13 }}>
                {t(type)}
            </Text>
        </Box>
    );
};

const WarehouseServiceDeskHistory = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const language = useSelector((state) => state.settings.language);
    const dateLocale = localeSettings[language]?.dateLocale;

    const [selectedRelation, setSelectedRelation] = useState(null);
    const [sortId, setSortId] = useState(SORT_OPTIONS[0].id);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const submitSearch = () => setSearch(searchInput.trim());

    const activeSort =
        SORT_OPTIONS.find((o) => o.id === sortId) || SORT_OPTIONS[0];

    const { data: relationsData, isFetching: relationsFetching } =
        useGetWarehouseRelationsWithTicketStatsQuery({
            sortBy: activeSort.sortBy,
            sortOrder: activeSort.sortOrder,
            search,
        });
    const relations = relationsData?.data ?? [];

    const [detailTicketId, setDetailTicketId] = useState(null);
    const detailOpen = detailTicketId !== null;
    const closeTicketDetail = () => setDetailTicketId(null);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions,
            styles: { main: { overflow: "visible" } },
        });
    }, []);

    // ShDataGrid2 bails when body is empty, but it always adds searchString + sort
    // so we don't need a marker key. The relation id rides on the URL via itemId.
    const gridConfig = useMemo(() => ({}), []);

    const defaultSort = useMemo(
        () => ({ field: "closedAt", type: "DESC" }),
        []
    );

    const columns = useMemo(
        () => [
            {
                field: "title",
                headerName: t('name'),
                flex: 2,
                renderCell: (params) => {
                    if (params.row.skeleton) return <ShTextSkeleton />;

                    return (
                        <Box
                            sx={{
                                display:"flex",
                                alignItems:"center",
                                gap:1.5,
                            }}
                        >
                            <PriorityBadge
                                priority={params.row.priority}
                            />
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:"column",
                                }}
                            >
                                <Text noBreak>{params.row.title}</Text>
                                <Text light bold>{params.row.ticketNumber}</Text>
                            </Box>
                        </Box>
                    );
                },
            },
            {
                field: "type",
                headerName: t("type"),
                flex: 1,
                renderCell: (params) => {
                    if (params.row.skeleton) return <ShTextSkeleton />;
                    return <TypeBadge type={params.row.type} />;
                },
            },
            {
                field: "closedAt",
                headerName: t("closed"),
                flex: 1,
                renderCell: (params) => {
                    if (params.row.skeleton) return <ShTextSkeleton />;
                    const ts = params.row.closedAt || params.row.updatedAt;
                    if (!ts) return <Text light>—</Text>;
                    return (
                        <Text light>
                            {formatDistanceToNow(new Date(ts), {
                                addSuffix: true,
                                locale: dateLocale,
                            })}
                        </Text>
                    );
                },
            },
            {
                field: "phoneView",
                headerName: "PhoneView",
                cellClassName: "phone-column--cell",
                renderCell: (params) => {
                    if (params.row.skeleton) return <ShTextSkeleton />;
                    return (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                width: 1,
                                py: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Text bold>#{params.row.ticketNumber}</Text>
                                <TypeBadge type={params.row.type} />
                            </Box>
                            <Text noBreak>{params.row.title}</Text>
                        </Box>
                    );
                },
            },
        ],
        [t, dateLocale]
    );

    const handleRowClick = (row) => {
        if (!row?.row?.id) return;
        setDetailTicketId(row.row.id);
    };

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "420px 1fr",
                minHeight: 1,
                "@media screen and (max-width: 63.9375em)": {
                    gridTemplateColumns: "1fr",
                },
            }}
        >
            {/* Left — relations list */}
            <Box
                sx={{
                    borderRight: `2px solid ${
                        theme.palette.mode === "dark"
                            ? colors.grey[400]
                            : colors.grey[100]
                    }`,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    "@media screen and (max-width: 63.9375em)": {
                        maxHeight: 360,
                    },
                }}
            >
                <Box
                    sx={{
                        pt: 4,
                        px: 4,
                        pb: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    <Subheading>{t("history")}</Subheading>

                    <Box
                        sx={{
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            pl: 1.5,
                            pr: 0.5,
                            borderRadius: 4,
                            background:
                                theme.palette.mode === "dark"
                                    ? colors.grey[400]
                                    : colors.grey[100],
                        }}
                    >
                        <SearchOutlinedIcon
                            sx={{
                                fontSize: 18,
                                color: colors.txt["secondary"],
                            }}
                        />
                        <InputBase
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") submitSearch();
                            }}
                            placeholder={t("search_relation")}
                            sx={{
                                flex: 1,
                                fontSize: 14,
                                fontFamily: "Roboto, sans-serif",
                                color: colors.txt["primary"],
                            }}
                        />
                        <DropdownBaseV2
                            displayName="label"
                            options={SORT_OPTIONS.map((o) => ({
                                id: o.id,
                                label: t(o.labelKey),
                            }))}
                            selected={SORT_OPTIONS.findIndex(
                                (o) => o.id === sortId
                            )}
                            onChange={(idx) => {
                                if (idx === -1) return;
                                setSortId(SORT_OPTIONS[idx].id);
                            }}
                            defaultStyling
                            closeOnSelection
                            disallowDeselection
                            stopPropagation
                            styles={{
                                optionSx: {
                                    paddingX: 1,
                                    py: 0,
                                    borderRadius: 3,
                                    height: 32,
                                },
                            }}
                        >
                            {({ onClick }) => (
                                <ButtonBase
                                    onClick={onClick}
                                    sx={{
                                        ml: "auto",
                                        height: 24,
                                        borderRadius: 4,
                                        background:
                                            theme.palette.mode === "dark"
                                                ? colors.txt["secondary"]
                                                : colors.grey[400],
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        px: 1.5,
                                        gap: 1,
                                    }}
                                >
                                    <Text
                                        color={
                                            theme.palette.mode === "dark"
                                                ? null
                                                : colors.txt["light"]
                                        }
                                        semibold
                                        sx={{ 
                                            fontSize: 13 
                                        }}
                                    >
                                        {t(activeSort.labelKey)}
                                    </Text>
                                </ButtonBase>
                            )}
                        </DropdownBaseV2>
                    </Box>
                </Box>

                <Box 
                    sx={{ 
                        flex: 1, 
                        overflowY: "auto",
                    }}
                >
                    {!relationsFetching && relations.length === 0 && (
                        <Box
                            sx={{
                                py: 6,
                                px: 4,
                                textAlign: "center",
                                color: colors.txt["secondary"],
                            }}
                        >
                            <Text light>
                                {search
                                    ? t("no_results")
                                    : t("no_relations_with_tickets")}
                            </Text>
                        </Box>
                    )}

                    {relationsFetching &&
                        relations.length === 0 &&
                        [1, 2, 3, 4, 5].map((i) => (
                            <Box
                                key={`skeleton-${i}`}
                                sx={{
                                    px: 4,
                                    py: 3,
                                    borderBottom: `2px solid ${
                                        theme.palette.mode === "dark"
                                            ? colors.grey[400]
                                            : colors.grey[100]
                                    }`,
                                }}
                            >
                                <ShTextSkeleton />
                            </Box>
                        ))}

                    {relations.map((relation) => (
                        <RelationHistoryCard
                            key={relation.id}
                            relation={relation}
                            onClick={() => setSelectedRelation(relation)}
                            selected={selectedRelation?.id === relation.id}
                        />
                    ))}
                </Box>
            </Box>

            {/* Right — closed ticket grid for the selected relation */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                }}
            >
                {!selectedRelation ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 6,
                            py: 12,
                            textAlign: "center",
                            color: colors.txt["secondary"],
                        }}
                    >
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: "50%",
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[400]
                                        : colors.grey[100],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: colors.txt["primary"],
                                mb: 2,
                            }}
                        >
                            <HistoryIcon sx={{ fontSize: 36 }} />
                        </Box>
                        <Subheading>{t("no_relation_selected")}</Subheading>
                        <Text align="center" light sx={{ maxWidth: 360 }}>
                            {t("pick_relation_to_view_history")}
                        </Text>
                    </Box>
                ) : (
                    <ShDataGrid2
                        key={`${theme.palette.mode}-${selectedRelation.id}`}
                        itemId={selectedRelation.id}
                        content={useGetWarehouseRelationTicketHistoryQuery}
                        config={gridConfig}
                        title={selectedRelation.name}
                        gridOptions={gridOptions}
                        sortModel={defaultSort}
                        columns={columns}
                        onRowClick={handleRowClick}
                        sideCarLeft={420}
                    />
                )}
            </Box>

            <MTicketDetailBottom
                open={detailOpen}
                onClose={closeTicketDetail}
                ticketId={detailTicketId}
                readOnly
                viewer="fulfilmentcenter"
            />
        </Box>
    );
};

export default WarehouseServiceDeskHistory;